import React from "react";
import { TableInput } from "components/common/input";
import { PrimayDropdown, MultiDropdown } from "components/common/dropdown";
import {
  RegionsNameNum,
  ReligionConstant,
  SearchOptionConstant,
} from "service/constant/burialConstant";
import type { IBurialAdd } from "service/interface/burial";

interface Props {
  data: IBurialAdd;
  setData: React.Dispatch<React.SetStateAction<IBurialAdd>>;
}

export function PlaceInfoTable({ data, setData }: Props) {
  const handleNameInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({
      ...prev,
      name: e.target.value,
    }));
  };

  const handlePublicInput = (check: boolean) => {
    setData((prev) => ({
      ...prev,
      isPublic: check,
    }));
  };

  const handleHomepageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({
      ...prev,
      homepage: e.target.value,
    }));
  };

  const handleOperatingHoursInput = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setData((prev) => ({
      ...prev,
      operatingHours: e.target.value,
    }));
  };

  const handlePhoneNumInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({
      ...prev,
      telephone: e.target.value,
    }));
  };

  const handleAddressInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData((prev) => ({
      ...prev,
      address: e.target.value,
    }));
  };

  const handleRegionDropdown = (id: number) => {
    setData((prev) => ({
      ...prev,
      region: id,
    }));
  };

  const handleReligionDropdown = (id: number) => {
    setData((prev) => ({
      ...prev,
      religion: id,
    }));
  };

  const handleSearchOptionDropdown = (id: number) => {
    if (id === 999) {
      setData((prev) => ({
        ...prev,
        SearchOptions: [],
      }));
    } else {
      setData((prev) => ({
        ...prev,
        SearchOptions: prev.SearchOptions.includes(id)
          ? prev.SearchOptions.filter((option) => option !== id)
          : [...prev.SearchOptions, id],
      }));
    }
  };

  return (
    <div className="mb-[64px]">
      <div className="mb-[16px] flex items-center justify-between">
        <p className="m-0 text-[24px] font-semibold leading-[30px] text-black">
          추모공원 정보
        </p>
      </div>
      <div className="w-full border-t-[2px] border-t-black p-0">
        <div className="flex h-[86px] w-full border-b-[1px] border-b-gray-1">
          <div className="flex h-full w-[148px] items-center justify-center bg-[#f4f4f4] text-M3 text-gray-4">
            시설명
          </div>
          <div className="flex h-full w-[318px] items-center px-[12px] text-M3 text-gray-5">
            <TableInput
              value={data.name}
              onChange={handleNameInput}
              placeholder="업체명을 입력해 주세요."
            />
          </div>
          <div className="flex h-full w-[164px] items-center justify-center bg-[#f4f4f4] text-M3 text-gray-4">
            운영 형태
          </div>
          <div className="flex h-full w-[303px] items-center gap-[48px] px-[24px] text-M3 text-gray-5">
            <label
              htmlFor="isPublic"
              className="flex cursor-pointer items-center gap-[8px]"
            >
              <input
                type="radio"
                id="isPublic"
                checked={data.isPublic}
                onChange={() => handlePublicInput(true)}
                className="cursor-pointer text-blue-1 focus:ring-white"
              />
              <div className="m-0 text-M3 text-[#494949]">공설</div>
            </label>
            <label
              htmlFor="isNotPublic"
              className="flex cursor-pointer items-center gap-[8px]"
            >
              <input
                type="radio"
                id="isNotPublic"
                onChange={() => handlePublicInput(false)}
                checked={!data.isPublic}
                className="cursor-pointer text-blue-1 focus:ring-white"
              />
              <div className="m-0 text-M3 text-[#494949]">사설</div>
            </label>
          </div>
        </div>
        <div className="flex h-[86px] w-full border-b-[1px] border-b-gray-1">
          <div className="flex h-full w-[148px] items-center justify-center bg-[#f4f4f4] text-M3 text-gray-4">
            홈페이지
          </div>
          <div className="flex h-full w-[318px] items-center px-[12px] text-M3 text-gray-5">
            <TableInput
              value={data.homepage}
              onChange={handleHomepageInput}
              placeholder="홈페이지 주소를 입력해주세요."
            />
          </div>
          <div className="flex h-full w-[164px] items-center justify-center bg-[#f4f4f4] text-M3 text-gray-4">
            운영 시간
          </div>
          <div className="flex h-full w-[303px] items-center gap-[48px] px-[24px] text-M3 text-gray-5">
            <TableInput
              value={data.operatingHours}
              onChange={handleOperatingHoursInput}
              placeholder="09:00 ~ 18:00"
            />
          </div>
        </div>
        <div className="flex h-[86px] w-full border-b-[1px] border-b-gray-1">
          <div className="flex h-full w-[148px] items-center justify-center bg-[#f4f4f4] text-M3 text-gray-4">
            전화번호
          </div>
          <div className="flex h-full w-[785px] items-center px-[12px] text-M3 text-gray-5">
            <TableInput
              value={data.telephone}
              onChange={handlePhoneNumInput}
              placeholder="연락처를 입력해 주세요."
            />
          </div>
        </div>
        <div className="flex h-[86px] w-full border-b-[1px] border-b-gray-1">
          <div className="flex h-full w-[148px] items-center justify-center bg-[#f4f4f4] text-M3 text-gray-4">
            주소
          </div>
          <div className="flex h-full w-[785px] items-center px-[12px] text-M3 text-gray-5">
            <TableInput
              value={data.address}
              onChange={handleAddressInput}
              placeholder="주소를 입력해 주세요."
            />
          </div>
        </div>
        <div className="flex h-[86px] w-full border-b-[1px] border-b-gray-1">
          <div className="flex h-full w-[148px] items-center justify-center bg-[#f4f4f4] text-M3 text-gray-4">
            지역
          </div>
          <div className="flex h-full w-[785px] items-center px-[12px] text-M3 text-gray-5">
            <PrimayDropdown
              label="지역을 선택해 주세요."
              selectMenu={RegionsNameNum[data.region]}
              onClick={handleRegionDropdown}
              menuList={[
                { id: 1, name: "서울특별시" },
                { id: 2, name: "경기남부" },
                { id: 3, name: "경기북부" },
                { id: 4, name: "강원도" },
                { id: 5, name: "경상남도" },
                { id: 6, name: "경상북도" },
                { id: 7, name: "광주광역시" },
                { id: 8, name: "대구광역시" },
                { id: 9, name: "세종특별자치시" },
                { id: 10, name: "울산광역시" },
                { id: 11, name: "인천광역시" },
                { id: 12, name: "전라남도" },
                { id: 13, name: "전라북도" },
                { id: 14, name: "제주특별자치도" },
                { id: 15, name: "충청남도" },
                { id: 16, name: "충청북도" },
              ]}
            />
          </div>
        </div>
        <div className="flex h-[86px] w-full border-b-[1px] border-b-gray-1">
          <div className="flex h-full w-[148px] items-center justify-center bg-[#f4f4f4] text-M3 text-gray-4">
            종교 시설
          </div>
          <div className="flex h-full w-[785px] items-center px-[12px] text-M3 text-gray-5">
            <PrimayDropdown
              label="종교시설을 선택해 주세요."
              selectMenu={ReligionConstant[data.religion]}
              onClick={handleReligionDropdown}
              menuList={[
                { id: 0, name: "해당 없음" },
                { id: 1, name: "종교 (十)" },
                { id: 2, name: "종교 (卍)" },
                { id: 3, name: "종교 (十, 卍)" },
              ]}
            />
          </div>
        </div>
        <div className="flex h-[86px] w-full border-b-[1px] border-b-gray-1">
          <div className="flex h-full w-[148px] items-center justify-center bg-[#f4f4f4] text-M3 text-gray-4">
            검색 태그
          </div>
          <div className="flex h-full w-[785px] items-center px-[12px] text-M3 text-gray-5">
            <MultiDropdown
              label="옵션 검색 태그를 선택해 주세요."
              selectMenu={data.SearchOptions.map(
                (option) => SearchOptionConstant[option] + "",
              )}
              selectId={data.SearchOptions.map((option) => option)}
              onClick={handleSearchOptionDropdown}
              menuList={[
                {
                  id: 999,
                  name: "해당없음",
                },
                { id: 1, name: "안치방법이 다양한" },
                { id: 2, name: "고품격 추모공간을 가진" },
                { id: 3, name: "가족과 함께하기 좋은" },
                { id: 4, name: "가족 단위 안치가 가능한" },
                { id: 5, name: "불교식 추모가 가능한" },
                { id: 6, name: "기독교식 추모가 가능한" },
                { id: 7, name: "천주교식 추모가 가능한" },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
